import type { ReactNode } from 'react';
import { cn } from '../cn.js';
import { EmptyState, Spinner } from './primitives.js';

export interface Column<T> {
  /** Clé stable, utilisée comme `key` React et pour le tri contrôlé. */
  readonly id: string;
  readonly header: ReactNode;
  readonly cell: (row: T) => ReactNode;
  /** Classes appliquées à la cellule et à l'en-tête (alignement, largeur). */
  readonly className?: string;
  readonly align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<T> {
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly onRowClick?: (row: T) => void;
  readonly loading?: boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
}

const ALIGN: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/**
 * Tableau de données des vues liste.
 *
 * Volontairement non virtualisé : les collections sont paginées côté API à 200
 * lignes maximum, une virtualisation ajouterait de la complexité pour rien.
 *
 * Le survol et le clic sur la ligne entière sont câblés parce que les maquettes
 * ne prévoient pas de colonne « action » ; un `role="button"` et la gestion du
 * clavier restent nécessaires pour que la navigation reste accessible.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  emptyTitle = 'Aucun résultat',
  emptyDescription,
}: DataTableProps<T>) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[48rem] border-collapse">
          <thead>
            <tr className="border-b border-border-subtle">
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    'label-caps px-5 py-3.5 font-medium',
                    ALIGN[column.align ?? 'left'],
                    column.className,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                className={cn(
                  'border-b border-border-subtle/60 last:border-0',
                  onRowClick && 'cursor-pointer transition-colors hover:bg-surface-raised',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'px-5 py-4 text-sm text-text',
                      ALIGN[column.align ?? 'left'],
                      column.className,
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-text-muted">
          <Spinner />
          Chargement…
        </div>
      ) : null}

      {!loading && rows.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : null}
    </div>
  );
}

/** Pagination compacte, contrôlée par le parent. */
export function Pagination({
  page,
  pageCount,
  total,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  if (pageCount <= 1) {
    return <p className="px-1 py-3 text-xs text-text-dim">{total} résultat(s)</p>;
  }

  return (
    <div className="flex items-center justify-between px-1 py-3 text-xs text-text-muted">
      <span>
        Page <span className="font-mono text-text">{page}</span> / {pageCount} — {total} résultat(s)
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="rounded px-2.5 py-1 ring-1 ring-border-strong transition-colors hover:bg-surface-raised disabled:opacity-40"
        >
          Précédent
        </button>
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className="rounded px-2.5 py-1 ring-1 ring-border-strong transition-colors hover:bg-surface-raised disabled:opacity-40"
        >
          Suivant
        </button>
      </div>
    </div>
  );
}
